import React, { useEffect, useState, useRef } from 'react';
import LocationPointer from '../images/location-pointer.svg';

type Props = {
  user?: any;
  location: any;
  handlePlaceChanged(place): void;
  handlePlaceError(error): void;
  onCityChanged?: (loc: {name:string; lat:number; lng:number}) => void;
};

async function recordRestaurantSearch(
  userId: number | string,
  token?: string,
  clientRequestId?: string
): Promise<void> {
  try {
    if (!userId) {
      console.debug('recordRestaurantSearch skipped: no userId');
      return;
    }
    const base = process.env.REACT_APP_SERVER_URL ?? '';
    const url = `${base}/action/restaurant-search/${encodeURIComponent(String(userId))}`;

    const authToken = token ?? localStorage.getItem('accessToken') ?? '';

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({ clientRequestId }),
    });

    if (!res.ok) {
      console.debug('recordRestaurantSearch failed', res.status, await res.text().catch(() => ''));
    }
  } catch (err) {
    console.debug('recordRestaurantSearch error', err);
  }
}

const FindRestaurant: React.FC<Props> = (props) => {
  const [isLocationAvailable, setIsLocationAvailable] = useState(false);
  const [location, setLocation] = useState({ name: '', lat: 0, lng: 0 });
  const [restaurantName, setRestaurantName] = useState('');
  const [hasError, setHasError] = useState(false);

  const iconRef = useRef<HTMLImageElement | null>(null);
  const cityInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const applyPadding = () => {
      if (iconRef.current && cityInputRef.current) {
        const iconWidth = iconRef.current.getBoundingClientRect().width;
        cityInputRef.current.style.paddingLeft = `${iconWidth + 24}px`;
      }
    };
    applyPadding();
    window.addEventListener('resize', applyPadding);
    return () => window.removeEventListener('resize', applyPadding);
  }, []);

  const newLocation = async (place, name: string) => {
    var obj = {
      name: name,
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
    setLocation(obj);
    setIsLocationAvailable(true);
    props.onCityChanged?.(obj);
  };

  const handlePlaceChange = (place) =>{
    if (place.geometry && place.address_components) {

      const addressComponents = place.address_components;
      let fullName : string = '';

      const country = addressComponents.filter((c) => c.types.includes('country'));
      const state = addressComponents.filter((c) => c.types.includes('administrative_area_level_1'));
      const locality = addressComponents.filter((c) => c.types.includes('locality'));

      if (locality.length > 0){
        fullName = `${locality[0].long_name}, ${state[0]?.short_name ?? ''}, ${country[0]?.short_name ?? ''}`;
        newLocation(place, fullName);
        return;
      }

      const administrativeArea = addressComponents.filter((c) => c.types.includes('administrative_area_level_2'));
      if (administrativeArea.length > 0) {
        fullName = `${administrativeArea[0].long_name}, ${state[0]?.short_name ?? ''}, ${country[0]?.short_name ?? ''}`;
        newLocation(place, fullName);
        return;
      }
    }

    const empty = { name: '', lat: 0, lng: 0 };
    setLocation(empty);
    setIsLocationAvailable(false);
    props.onCityChanged?.(empty);
  };

  const onClearLocation = () => {
    setIsLocationAvailable(false);
    const empty = { name: '', lat: 0, lng: 0 };
    setLocation(empty);
    props.onCityChanged?.(empty);

    const cityInput = cityInputRef.current;
    if (cityInput) {
      cityInput.disabled = false;
      cityInput.value = '';
      cityInput.placeholder = 'Enter your desired city.';
      cityInput.focus();
    }
  };

  useEffect(() => {
    if (cityInputRef.current) {
      cityInputRef.current.value = props.location?.name || '';
    }
  }, [props.location?.name]);

  useEffect(() => {
    if (props.location.lat !== 0 && props.location.lng !== 0) {
      setLocation(props.location);
      setIsLocationAvailable(true);
    } else {
      setIsLocationAvailable(false);
    }
  }, [props.location]);

  useEffect(() => {
    const center = { lat: props.location.lat, lng: props.location.lng };

    const cityInput = cityInputRef.current as HTMLInputElement | null;
    if (!cityInput)
      return;

    const defaultBounds = {
      north: center.lat + 0.1,
      south: center.lat - 0.1,
      east: center.lng + 0.1,
      west: center.lng - 0.1,
    };

    const options = {
      bounds: defaultBounds,
      fields: ['address_components', 'geometry'],
      origin: center,
      strictBounds: false,
      types: ['geocode'],
    };

    const cityAutocomplete = new google.maps.places.Autocomplete(cityInput, options);
    const cityListener = cityAutocomplete.addListener('place_changed', function () {
      const place = (cityAutocomplete as any).getPlace();
      handlePlaceChange(place);
    });

    return () => {
      try {
        if (cityListener && typeof (cityListener as any).remove === 'function') {
          (cityListener as any).remove();
        } else {
          google.maps.event.clearInstanceListeners(cityAutocomplete);
        }
      } catch (e) {
        console.debug('cleanup cityAutocomplete listener error', e);
      }
    };
  }, [props.location, isLocationAvailable]);

  useEffect(() => {
    if (!isLocationAvailable) return;

    const center = { lat: location.lat, lng: location.lng };
    const circle = new google.maps.Circle({ center, radius: 50 * 1609.344 });
    const input = document.getElementById('restaurant-name') as HTMLInputElement;
    if (!input) return;

    const options = {
      bounds: circle.getBounds(),
      fields: ['place_id', 'address_components', 'geometry', 'name'],
      origin: center,
      strictBounds: true,
      types: ['establishment'],
    };

    const autocomplete = new google.maps.places.Autocomplete(input, options);

    const restaurantListener = autocomplete.addListener('place_changed', function () {
      const place = (autocomplete as any).getPlace();

      if (place.geometry && place.address_components && place.name && place.place_id) {
        // Extract address parts by type — NOT by positional index.
        // Positional indexing is fragile: component[2] could be a neighborhood
        // like a neighborhood instead of the city, causing reward address matching to fail.
        const getComponent = (types: string[]) =>
          place.address_components.find((c: any) =>
            types.some((t) => c.types.includes(t))
          );

        const streetNumber = getComponent(['street_number'])?.short_name ?? '';
        const route        = getComponent(['route'])?.short_name ?? '';
        const city         = getComponent(['locality'])?.long_name
                          ?? getComponent(['administrative_area_level_2'])?.long_name
                          ?? '';
        const state        = getComponent(['administrative_area_level_1'])?.short_name ?? '';

        // Produces e.g. "624 S La Brea Ave Minneapolis MN"
        const address = [streetNumber, route, city, state].filter(Boolean).join(' ');

        console.debug('[FindRestaurant] address built:', address, '| components:', place.address_components);

        const obj = {
          id: place.place_id,
          name: place.name,
          address,
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
          response: JSON.stringify(place),
        };

        console.debug('place_selected', { placeId: place.place_id, name: place.name });

        props.handlePlaceChanged(obj);
        setRestaurantName('');

        try {
          const clientRequestId =
            typeof crypto !== 'undefined' && (crypto as any).randomUUID
              ? (crypto as any).randomUUID()
              : `ts-${Date.now()}`;

          const userId = props.user?.id ?? (() => {
            const u = localStorage.getItem('user');
            if (!u) return '';
            try { return JSON.parse(u).id; } catch { return ''; }
          })();

          const token = props.user?.token;

          if (userId) {
            recordRestaurantSearch(userId, token, clientRequestId);
          } else {
            console.debug('recordRestaurantSearch skipped: no user available');
          }
        } catch (e) {
          console.debug('recordRestaurantSearch client error', e);
        }
      } else {
        props.handlePlaceError('Error!');
        setRestaurantName('');
      }
    });

    return () => {
      try {
        if (restaurantListener && typeof (restaurantListener as any).remove === 'function') {
          (restaurantListener as any).remove();
        } else {
          google.maps.event.clearInstanceListeners(autocomplete);
        }
      } catch (e) {
        console.debug('cleanup restaurantAutocomplete listener error', e);
      }
    };
  }, [location, isLocationAvailable]);

  const handleInputValue = (e) => {
    setRestaurantName(e.target.value);
    setHasError(false);
  };

  return (
    <div className="yolo-container text-center">
      <h3 className="find-restaurant-heading">Find the best tasting menu items at this restaurant...</h3>

      <div className="location-field">
        <img ref={iconRef} src={LocationPointer} alt="" className="loc-pin" />
        <input
          id="city-name"
          ref={cityInputRef}
          className="field xl-filed location-input"
          disabled={isLocationAvailable}
          placeholder={props.location.name ? props.location.name : 'Enter your desired city.'}
        />
        {isLocationAvailable && (
          <button type="button" className="clear-loc" aria-label="Clear location" onClick={onClearLocation}>
            ×
          </button>
        )}
      </div>

      <input
        autoFocus
        id="restaurant-name"
        className="field xl-filed location-input restaurant-input text-center mt-4 mb-4"
        disabled={!isLocationAvailable}
        placeholder={isLocationAvailable ? 'ENTER RESTAURANT NAME HERE' : 'Select City Above'}
        value={hasError ? "" : restaurantName}
        onChange={handleInputValue}
      />
    </div>
  );
};

export default FindRestaurant;
